import React, { useContext, useEffect, useState } from 'react';
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import { toastClick } from '../../composants/toast/CustomToast';
import { LoadingContext } from '../../composants/loadingContext';
import Loader from '../../composants/loader/Loader';

const Denonciation = () => {
    const {loading, setLoading} = useContext(LoadingContext);
    const [message, setMessage] = useState("");
    const [charCount, setCharCount] = useState(0);
    const [isAnonymous, setIsAnonymous] = useState(true);
    const minChars = 10;
    const maxChars = 1000;

    const checkTextareaValue = (event) => {
        const value = event.target.value;
        setMessage(value);
        setCharCount(value.length);
    };

    useEffect(() => {
        document.title = 'YouthFP | Signalement Confidentiel';
        setLoading(false);
    }, [setLoading]);

    const submitDenonciation = (event) => {
        event.preventDefault();
        
        if (message.trim().length < minChars) {
            toastClick("Erreur", `Le message doit contenir au moins ${minChars} caractères`);
            return;
        }

        setLoading(true);
        axiosInstance().post('public/denonciation', {
            contenu: message.trim(),
            id: null,
            dateDenonciation: Date.now(),
            utilisateur: null
        })
        .then(function (response) {
            setMessage("");
            setCharCount(0);
            toastClick("Envoyé avec succès", "Votre signalement a été reçu. Merci de votre confiance.");
            setLoading(false);
            setTimeout(() => { window.location.reload(); }, 3000);
        })
        .catch(function (error) {
            console.error('Erreur lors de l\'envoi:', error);
            toastClick("Échec de l'envoi", "Une erreur est survenue. Veuillez réessayer.");
            setLoading(false);
        });
    };

    const canSubmit = message.trim().length >= minChars && message.length <= maxChars;

    return (
        <>
            {loading && <Loader />}
            
            {/* Hero Section with Gradient */}
            <section className="relative pt-24 pb-12 bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full opacity-5 blur-3xl"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300 rounded-full opacity-5 blur-3xl"></div>
                </div>
                
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center text-white">
                        <div className="inline-block mb-4 px-4 py-2 bg-white bg-opacity-20 rounded-full backdrop-blur-sm">
                            <span className="text-sm font-medium">🔒 Confidentiel et Sécurisé</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                            Signalement Confidentiel
                        </h1>
                        <p className="text-lg md:text-xl opacity-95 max-w-2xl mx-auto leading-relaxed">
                            Votre voix compte. Signalez anonymement toute situation préoccupante en toute sécurité. 
                            Nous sommes là pour vous écouter et agir.
                        </p>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        
                        {/* Info Cards */}
                        <div className="grid md:grid-cols-3 gap-6 mb-12">
                            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-purple-500">
                                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-lg text-gray-800 mb-2">100% Anonyme</h3>
                                <p className="text-gray-600 text-sm">Votre identité reste protégée. Aucune information personnelle n'est collectée.</p>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-pink-500">
                                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-lg text-gray-800 mb-2">Sécurisé</h3>
                                <p className="text-gray-600 text-sm">Vos données sont cryptées et transmises en toute sécurité.</p>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-t-4 border-red-500">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-lg text-gray-800 mb-2">Action Rapide</h3>
                                <p className="text-gray-600 text-sm">Chaque signalement est traité avec sérieux et dans les plus brefs délais.</p>
                            </div>
                        </div>

                        {/* Form Card */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            {/* Form Header */}
                            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
                                <h2 className="text-2xl font-bold mb-2">Formulaire de Signalement</h2>
                                <p className="opacity-90">Décrivez la situation de manière détaillée. Plus vous donnez d'informations, mieux nous pourrons agir.</p>
                            </div>

                            {/* Form Body */}
                            <form onSubmit={submitDenonciation} className="p-8">
                                
                                {/* Anonymous Toggle */}
                                <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isAnonymous}
                                            onChange={(e) => setIsAnonymous(e.target.checked)}
                                            className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                                        />
                                        <span className="ml-3 text-gray-800 font-medium">
                                            Je souhaite rester anonyme
                                        </span>
                                    </label>
                                    <p className="text-sm text-gray-600 mt-2 ml-8">
                                        {isAnonymous 
                                            ? "Votre identité ne sera pas révélée." 
                                            : "Vous pouvez inclure vos coordonnées dans le message si vous le souhaitez."}
                                    </p>
                                </div>

                                {/* Textarea */}
                                <div className="mb-6">
                                    <label htmlFor="message" className="block text-gray-700 font-semibold mb-3">
                                        Votre signalement <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            name="message"
                                            id="message"
                                            value={message}
                                            onChange={checkTextareaValue}
                                            placeholder="Décrivez la situation : Qui ? Quoi ? Où ? Quand ? Toute information utile pour nous aider à comprendre et agir..."
                                            rows="12"
                                            maxLength={maxChars}
                                            required
                                            className="w-full px-6 py-4 text-gray-700 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-purple-500 focus:bg-white focus:ring-4 focus:ring-purple-100 resize-none transition-all duration-300"
                                        ></textarea>
                                        
                                        {/* Character counter */}
                                        <div className="absolute bottom-3 right-3 text-sm">
                                            <span className={`font-medium ${
                                                charCount < minChars 
                                                    ? 'text-red-500' 
                                                    : charCount > maxChars * 0.9 
                                                        ? 'text-orange-500' 
                                                        : 'text-gray-500'
                                            }`}>
                                                {charCount}
                                            </span>
                                            <span className="text-gray-400"> / {maxChars}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Helper text */}
                                    <div className="mt-3 flex items-start space-x-2">
                                        {charCount < minChars ? (
                                            <div className="flex items-center text-sm text-red-600">
                                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                Minimum {minChars} caractères requis (encore {minChars - charCount})
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-sm text-green-600">
                                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                Votre message est prêt à être envoyé
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Important Notice */}
                                <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-blue-700">
                                                <strong className="font-semibold">Important :</strong> En cas d'urgence médicale ou de danger immédiat, 
                                                veuillez contacter directement les services d'urgence ou notre ligne d'assistance 24h/24.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                                    <div className="text-sm text-gray-600">
                                        <svg className="w-4 h-4 inline mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Transmission sécurisée
                                    </div>
                                    
                                    <button
                                        type="submit"
                                        disabled={!canSubmit || loading}
                                        className={`px-8 py-4 rounded-xl font-semibold text-white text-lg shadow-lg transform transition-all duration-300 ${
                                            canSubmit && !loading
                                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:scale-105 hover:shadow-xl'
                                                : 'bg-gray-300 cursor-not-allowed'
                                        }`}
                                    >
                                        {loading ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Envoi en cours...
                                            </span>
                                        ) : (
                                            <span className="flex items-center">
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Envoyer le signalement
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Support Section */}
                        <div className="mt-12 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100">
                            <div className="text-center">
                                <h3 className="text-2xl font-bold text-gray-800 mb-4">Besoin d'aide immédiate ?</h3>
                                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                                    Si vous ou quelqu'un que vous connaissez êtes en danger, n'hésitez pas à contacter nos services d'urgence.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a href="tel:+237" className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        Ligne d'urgence 24/7
                                    </a>
                                    <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 hover:shadow-lg transition-all duration-300 hover:scale-105">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        Nous contacter
                                    </a>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
};

export default Denonciation;